import React from 'react';
import { Field } from 'formik';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { useExtendedFormikContext } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';
import GenericFormElement from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/GenericFormElement';

const Radio = (props) => {
  const {
    className,
    disabled,
    onChange,
    required,
    correct,
    someChecked,
  } = props;

  return (
    <Field
      name={props.name}
      validate={props.validate}
      required={required}
    >
      {({ field, form }) => {
        const name = `${props.name}--${props.value}`;
        const classes = c('WebformRadios__radio__input', {
          'icon-before': true,
          'icon-radio': true,
          // These double equals instead of triple equals are on purpose.
          // JavaScript object keys are always strings even though they may be
          // represented as numbers.
          /* eslint-disable */
          'icon-variant-0': !(field.value && field.value == props.value),
          'icon-variant-1': field.value && field.value == props.value,
          /* eslint-enable */
          'correct': someChecked && correct,
          'disabled': someChecked,
        });

        if (props.value === '__other__') {
          const inputClasses = c({
            hidden: (field.value !== '__other__'),
          });

          return (
            <label htmlFor={name} className={classes}>
              <input
                {...field}
                type="radio"
                id={name}
                value={props.value}
                disabled={disabled || someChecked}
                className={className}
                required={required}
                onChange={(event) => {
                  if (onChange) {
                    onChange(props.value);
                  }

                  field.onChange(event);
                }}
              />
              <span>
                <span>{props.otherLabel ? props.otherLabel : props.label}</span>
                {props.description && <span>{props.description}</span>}
              </span>
              <textarea
                placeholder={props.otherPlaceholder}
                className={inputClasses}
                onChange={(e) => {
                  form.setFieldValue(`${props.name}--other`, e.target.value);
                }}
              />
            </label>
          );
        }

        return (
          <label htmlFor={name} className={classes}>
            <input
              {...field}
              type="radio"
              id={name}
              value={props.value}
              disabled={disabled}
              className={className}
              required={required}
              onChange={(event) => {
                if (onChange) {
                  onChange(props.value);
                }

                field.onChange(event);
              }}
            />
            <span>
              <span>{props.label}</span>
              {props.description && <span>{props.description}</span>}
            </span>
          </label>
        );
      }}
    </Field>
  );
};

const RadioButtons = (props) => {
  const {
    title,
    required,
    description,
    placeholder,
    fieldName,
    validation,
    type,
    options,
    descriptions = {},
    className,
    wrapperClassName,
    disabled = false,
    otherLabel,
    otherPlaceholder,
    onChange,
    includeFieldSet,
    correctAnswer,
    someChecked,
  } = props;

  const { t } = useTranslation();

  const {
    errors,
    touched,
    displayRequiredIndicator,
  } = useExtendedFormikContext();

  const isTouched = touched[fieldName];
  const error = errors[fieldName];
  const errorId = `${props.fieldName}--error`;
  const descriptionId = `${props.fieldName}--description`;

  const handleValidation = (value) => {
    if (validation) {
      return validation(value);
    }

    if (required && !value) {
      return t(['webform.components.radio_buttons.required', 'webform.required', 'webform_required']);
    }
  };

  return (
    <GenericFormElement
      className={c('RadioButtons', wrapperClassName)}
      title={title}
      required={required}
      description={description}
      fieldName={fieldName}
      touched={isTouched}
      error={error}
      errorId={errorId}
      descriptionId={descriptionId}
      displayRequiredIndicator={displayRequiredIndicator}
      includeFieldSet={includeFieldSet}
    >
      {
        Object.keys(options).map((item) => {
          const label = typeof options[item] === 'object' ? options[item].label : options[item];
          const value = typeof options[item] === 'object' ? options[item].value : item;
          const optionDisabled = disabled || (typeof options[item] === 'object' && options[item].disabled);
          const description = descriptions[value] || (typeof options[item] === 'object' && options[item].description) || null;

          return (
            <Radio
              key={value}
              label={label}
              description={description}
              className={c(className, { 'error': error && touched })}
              name={fieldName}
              type={type || 'text'}
              value={value}
              placeholder={placeholder}
              validate={handleValidation}
              otherLabel={otherLabel}
              otherPlaceholder={otherPlaceholder}
              disabled={optionDisabled}
              onChange={onChange}
              errorId={errorId}
              error={error}
              isTouched={isTouched}
              required={required}
              correct={correctAnswer === value}
              someChecked={someChecked}
            />
          );
        })
      }
    </GenericFormElement>
  );
};

export default RadioButtons;
